import React, { createContext, useContext, useEffect, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { useSelector } from 'react-redux';

const SignalRContext = createContext(null);

const MAX_RETRY_DELAY_MS = 30000;

export function SignalRProvider({ children }) {
  const [connection, setConnection] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const user = useSelector((state) => state.user.user);

  useEffect(() => {
    let cancelled = false;
    let activeConnection = null;
    let retryTimeout = null;

    const buildConnection = () => {
      const newConn = new signalR.HubConnectionBuilder().withUrl(process.env.NODE_ENV === 'development' ?
        (process.env.REACT_APP_SIGNALR_DIRECT_URL ? `${process.env.REACT_APP_SIGNALR_DIRECT_URL}/Api/hubs/app` : `https://localhost:7145/Api/hubs/app` 
      ) : '/Api/hubs/app').withAutomaticReconnect().build();

      newConn.onreconnecting((error) => {
        console.warn('SignalR reconnecting:', error);
        setConnectionStatus('Reconnecting');
      });

      newConn.onreconnected(() => {
        setConnectionStatus('Connected');
      });

      newConn.onclose((error) => {
        console.error('SignalR connection closed permanently:', error);
        setConnectionStatus('Disconnected');
        setConnection(null);
      });

      return newConn;
    };

    const connectWithRetry = async (attempt = 0) => {
      if (cancelled || !user?.id) return;

      const newConn = buildConnection();
      activeConnection = newConn;
      setConnectionStatus('Connecting');
      try {
        await newConn.start();
        if (cancelled) {
          newConn.stop();
          return;
        }
        setConnectionStatus('Connected');
        setConnection(newConn);
      } catch (err) {
        console.error(`SignalR initialization failed (attempt ${attempt + 1}):`, err);
        setConnectionStatus('Failed');

        const delay = Math.min(1000 * 2 ** attempt, MAX_RETRY_DELAY_MS);
        retryTimeout = setTimeout(() => connectWithRetry(attempt + 1), delay);
      }
    };

    connectWithRetry();

    return () => {
      cancelled = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      if (activeConnection) activeConnection.stop();
    };
  }, [user]);

  return <SignalRContext.Provider value={{ connection, connectionStatus }}>{children}</SignalRContext.Provider>;
}

export function useSignalR() {
  const context = useContext(SignalRContext);
  if (context === null) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
}
